import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import SessionExpiredDialog from './components/SessionExpiredDialog';
import PrezziarioPdfPage from './pages/PrezziarioPdfPage';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import DashboardPage from './pages/DashboardPage';
import EmployeesPage from './pages/EmployeesPage';
import EmployeeDetailPage from './pages/EmployeeDetailPage';
import EquipmentPage from './pages/EquipmentPage';
import EquipmentDetailPage from './pages/EquipmentDetailPage';
import InventoryPage from './pages/InventoryPage';
import SitesPage from './pages/SitesPage';
import AttendancePage from './pages/AttendancePage';
import LeavesPage from './pages/LeavesPage';
import UsersPage from './pages/UsersPage';
import NotificationsPage from './pages/NotificationsPage';
import ScadenzePage from './pages/ScadenzePage';
import CompanyDocsPage from './pages/CompanyDocsPage';
import SiteAccountingPage from './pages/SiteAccountingPage';
import SALPage from './pages/SALPage';
import ExpenseClaimsPage from './pages/ExpenseClaimsPage';
import ProjectDocsPage from './pages/ProjectDocsPage';
import WorkJournalPage from './pages/WorkJournalPage';
import ReportsPage from './pages/ReportsPage';
import ProductionDiaryPage from './pages/ProductionDiaryPage';
import PersonalDocsPage from './pages/PersonalDocsPage';
import IncidentsPage from './pages/IncidentsPage';
import SafetyPointsPage from './pages/SafetyPointsPage';
import ProjectCalendarPage from './pages/ProjectCalendarPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import QuotesPage from './pages/QuotesPage';
import DataImportPage from './pages/DataImportPage';
import CalendarPage from './pages/CalendarPage';
import SuppliersPage from './pages/SuppliersPage';
import DDTPage from './pages/DDTPage';
import OrdersPage from './pages/OrdersPage';
import RDOPage from './pages/RDOPage';
import RegardsPage from './pages/RegardsPage';
import SafetyDevicesPage from './pages/SafetyDevicesPage';
import EmailSettingsPage from './pages/EmailSettingsPage';
import PortalsPage from './pages/PortalsPage';
import InvoicesPage from './pages/InvoicesPage';
import ScadenziarioPage from './pages/ScadenziarioPage';
import EmployeeReportsPage from './pages/EmployeeReportsPage';
import SegnalazioniPage from './pages/SegnalazioniPage';
import OnCallPage from './pages/OnCallPage';
import EmployeeAreaPage from './pages/EmployeeAreaPage';
import SitePlanningPage from './pages/SitePlanningPage';
import DocumentsPage from './pages/DocumentsPage';
import ContactsPage from './pages/ContactsPage';
import PriceListsPage from './pages/PriceListsPage';
import SESMatricesPage from './pages/SESMatricesPage';
import ClientOrdersPage from './pages/ClientOrdersPage';
import EmailArchivePage from './pages/EmailArchivePage';
import TrainingOverviewPage from './pages/TrainingOverviewPage';
import PublicRecruitmentPage from './pages/PublicRecruitmentPage';
import PrivacyRecruitmentPage from './pages/PrivacyRecruitmentPage';
import RecruitmentPage from './pages/RecruitmentPage';
import AvailabilityFormPage from './pages/AvailabilityFormPage';
import BankStatementsPage from './pages/BankStatementsPage';
import SALFiumicinoPage from './pages/SALFiumicinoPage';
import GanttPage from './pages/GanttPage';
import PayslipsPage from './pages/PayslipsPage';
import PaymentCardsPage from './pages/PaymentCardsPage';
import MyCardsPage from './pages/MyCardsPage';
import './App.css';

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-pulse text-slate-400">Caricamento...</div></div>;
  return user ? children : <Navigate to="/login" />;
}

function AdminRoute({ children }) {
  const { user } = useAuth();
  if (user?.role_name !== 'admin') return <Navigate to="/" />;
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-pulse text-slate-400">Caricamento...</div></div>;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" /> : <LoginPage />} />
      <Route path="/lavora-con-noi" element={<PublicRecruitmentPage />} />
      <Route path="/lavora-con-noi/privacy" element={<PrivacyRecruitmentPage />} />
      <Route path="/lavora-con-noi/riprendi/:token" element={<PublicRecruitmentPage />} />
      <Route path="/lavora-con-noi/:seoSlug" element={<PublicRecruitmentPage />} />
      <Route path="/disponibilita/:token" element={<AvailabilityFormPage />} />
      <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
        <Route index element={<DashboardPage />} />
        <Route path="scadenze" element={<ScadenzePage />} />
        <Route path="payment-cards" element={<PaymentCardsPage />} />
        <Route path="le-mie-carte" element={<MyCardsPage />} />
        <Route path="employees" element={<EmployeesPage />} />
        <Route path="training-overview" element={<TrainingOverviewPage />} />
        <Route path="recruitment" element={<RecruitmentPage />} />
        <Route path="employees/:id" element={<EmployeeDetailPage />} />
        <Route path="equipment" element={<EquipmentPage />} />
        <Route path="equipment/:id" element={<EquipmentDetailPage />} />
        <Route path="inventory" element={<InventoryPage />} />
        <Route path="sites" element={<SitesPage />} />
        <Route path="site-accounting" element={<SiteAccountingPage />} />
        <Route path="sal" element={<SALPage />} />
        <Route path="ses-matrices" element={<SESMatricesPage />} />
        <Route path="client-orders" element={<ClientOrdersPage />} />
        <Route path="email-archive" element={<EmailArchivePage />} />
        <Route path="sal-fiumicino" element={<SALFiumicinoPage />} />
        <Route path="gantt" element={<GanttPage />} />
        <Route path="buste-paga" element={<PayslipsPage />} />
        <Route path="bank-statements" element={<BankStatementsPage />} />
        <Route path="expense-claims" element={<ExpenseClaimsPage />} />
        <Route path="project-docs" element={<ProjectDocsPage />} />
        <Route path="work-journal" element={<WorkJournalPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="production-diary" element={<ProductionDiaryPage />} />
        <Route path="my-docs" element={<PersonalDocsPage />} />
        <Route path="company-docs" element={<CompanyDocsPage />} />
        <Route path="attendance" element={<AttendancePage />} />
        <Route path="leaves" element={<LeavesPage />} />
        <Route path="users" element={<AdminRoute><UsersPage /></AdminRoute>} />
        <Route path="incidents" element={<IncidentsPage />} />
        <Route path="safety-points" element={<SafetyPointsPage />} />
        <Route path="project-calendar" element={<ProjectCalendarPage />} />
        <Route path="projects" element={<ProjectCalendarPage />} />
        <Route path="projects/:projectId" element={<ProjectDetailPage />} />
        <Route path="quotes" element={<QuotesPage />} />
        <Route path="data-import" element={<DataImportPage />} />
        <Route path="calendario" element={<CalendarPage />} />
        <Route path="fornitori" element={<SuppliersPage />} />
        <Route path="ddt" element={<DDTPage />} />
        <Route path="ordini" element={<OrdersPage />} />
        <Route path="rdo" element={<RDOPage />} />
        <Route path="regards" element={<RegardsPage />} />
        <Route path="safety-devices" element={<SafetyDevicesPage />} />
        <Route path="portals" element={<PortalsPage />} />
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="scadenziario" element={<ScadenziarioPage />} />
        <Route path="segnalazioni" element={<SegnalazioniPage />} />
        <Route path="oncall" element={<OnCallPage />} />
        <Route path="area-dipendente" element={<EmployeeAreaPage />} />
        <Route path="site-planning/:siteId" element={<SitePlanningPage />} />
        <Route path="documents" element={<DocumentsPage />} />
        <Route path="contacts" element={<ContactsPage />} />
        <Route path="prezziario" element={<PriceListsPage />} />
        <Route path="prezziario-pdf" element={<PrezziarioPdfPage />} />
        <Route path="employee-reports" element={<EmployeeReportsPage />} />
        <Route path="email-settings" element={<AdminRoute><EmailSettingsPage /></AdminRoute>} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="change-password" element={<ChangePasswordPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SessionExpiredDialog />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
